// @owma/common-web/utils/ui/core — 뷰어-안전(host 앱 의존 없는) UI 헬퍼.
// email-thread-viewer 는 반드시 이 모듈만 import 한다(barrel index.tsx 는 @owma/kit·conf·services
// 결합 코드를 포함하므로 뷰어 모듈 그래프에 넣지 말 것 — platform 스왑 시 해석 불가).
import React, { ReactNode, ReactElement } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
// ─── colors ──────────────────────────────────────────────────────────────────

export const colors: string[] = [
  '#A9624A',
  '#D06B64',
  '#F83A22',
  '#FA573C',
  '#FF7537',
  '#FF950F',
  '#00BB65',
  '#008247',
  '#43A608',
  '#6BBC00',
  '#FFA600',
  '#FFBC0A',
  '#67AC8F',
  '#61BCC4',
  '#7AA4C8',
  '#4986E7',
  '#6D6FDE',
  '#8562D6',
  '#8D8D8D',
  '#AB8E92',
  '#A7767D',
  '#F46594',
  '#F46594',
  '#7446B7',
]

// ─── stringToHash / getColorByString ────────────────────────────────────────

export function stringToHash(s: string | null): number {
  let h = 0
  if (s != null) {
    for (let i = 0; i < s.length; i++) {
      h = (Math.imul(31, h) + s.charCodeAt(i)) | 0
    }
  }
  return h
}

export function getColorByString(str: string): string {
  const absHash = Math.abs(stringToHash(str))
  return colors[Math.abs(((absHash % colors.length) - 1) % colors.length)]
}

// ─── unicode helpers ──────────────────────────────────────────────────────────

export function unicodeCharAt(str: string, index: number): string {
  const first = str.charCodeAt(index)
  let second

  if (first >= 0xd800 && first <= 0xdbff && str.length > index + 1) {
    second = str.charCodeAt(index + 1)

    if (second >= 0xdc00 && second <= 0xdfff) {
      return str.substring(index, index + 2)
    }
  }

  return str[index]
}

export function unicodeSlice(str: string, start: number, end: number, words?: boolean): string {
  let accumulator = ''
  let character
  let stringIndex = 0
  let unicodeIndex = 0
  let nextSpace = -1
  const { length } = str

  str = str.trim()

  while (stringIndex < length) {
    character = unicodeCharAt(str, stringIndex)

    if (unicodeIndex >= start && unicodeIndex < end) {
      accumulator += character
    } else {
      break
    }

    stringIndex += character.length
    unicodeIndex += 1

    nextSpace = words ? str.indexOf(' ', nextSpace + 1) : -1
    stringIndex = nextSpace > 0 ? nextSpace + 1 : stringIndex
  }

  return accumulator
}

export function getPureText(
  name?: string | null,
  email?: string | null,
  emptyName?: string,
): string {
  let tmp: string[] = []
  if (name != null && name.length > 0) {
    tmp = name.split(/[\s\r\n,.(){}\[\]_\+\-]+/gm)
  } else if (email != null && email.length > 0) {
    tmp = email.split(/[\s\r\n,.(){}\[\]_@\+\-]+/gm)
  } else if (emptyName != null) {
    tmp.push(emptyName)
  }

  let str = ''
  tmp.forEach((txt) => {
    if (/^(\p{L}|\d)/gu.test(txt) === true) {
      str += txt.charAt(0)
    }
  })

  return str
}

// ─── InitialAvatar ────────────────────────────────────────────────────────────

export interface InitialAvatarProps {
  name?: string
  color?: string | null
  seed?: number
  charCount?: number
  textColor?: string
  height?: number
  width?: number
  fontSize?: string | number
  fontWeight?: number
  fontFamily?: string
  radius?: number
  useWords?: boolean
  emptyName?: string
}

export function getInitialAvatarSrcByProps(props: InitialAvatarProps): string {
  const ownProps: InitialAvatarProps = {
    name: 'Name',
    color: null,
    seed: 0,
    charCount: 1,
    textColor: '#ffffff',
    height: 40,
    width: 40,
    fontSize: '18px',
    fontWeight: 700,
    fontFamily:
      'Pretendard GOV Variable, Pretendard GOV, -apple-system, BlinkMacSystemFont, system-ui, Roboto, "Helvetica Neue", "Segoe UI", "Apple SD Gothic Neo", "Noto Sans KR", "Malgun Gothic", "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", sans-serif',
    radius: 0,
    ...props,
  }

  const {
    width,
    height,
    textColor,
    fontFamily,
    fontSize,
    fontWeight,
    charCount,
    useWords,
    name,
    color,
    seed,
    radius: borderRadius,
  } = ownProps

  let initial = unicodeSlice(name || 'Name', 0, charCount || 1, useWords || false).toUpperCase()
  let backgroundColor =
    color != null
      ? color
      : colors[Math.floor((initial.charCodeAt(0) + (seed || 0)) % colors.length)]

  if ((name || '').length === 0) {
    initial = ''
    ;[backgroundColor] = colors
  }

  const InitialSvg = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      pointerEvents="none"
      {...{
        width,
        height,
        style: {
          width,
          height,
          backgroundColor,
          borderRadius,
        },
      }}
    >
      <text
        y="50%"
        x="50%"
        dy="0.36em"
        pointerEvents="auto"
        fill={textColor}
        fontFamily={fontFamily}
        textAnchor="middle"
        style={{
          fontSize,
          fontWeight,
        }}
      >
        {initial}
      </text>
    </svg>
  )

  const encodedStr = encodeURIComponent(renderToStaticMarkup(<InitialSvg />)).replace(
    /%([0-9A-F]{2})/g,
    (match, p1) => {
      return String.fromCharCode(parseInt(`0x${p1}`, 16))
    },
  )

  const imgStr = window.btoa(encodedStr)
  const svgHtml = `data:image/svg+xml;base64,${imgStr}`

  return svgHtml
}

export function getInitialAvatarSrc(
  name?: string | null,
  email?: string | null,
  props?: InitialAvatarProps,
): string | null {
  const avatarProps: InitialAvatarProps = {
    ...props,
    charCount: 1,
  }

  if (props == null || props.name == null) {
    if (name == null && email == null && props?.emptyName == null) {
      return null
    }

    let str = getPureText(name, email, props?.emptyName)
    str = str.toUpperCase()

    if (str.length === 0) {
      str = '#'
    }

    avatarProps.name = str
  }

  const emptyName = props != null && props.emptyName != null ? props.emptyName : ''

  if (avatarProps.color == null) {
    avatarProps.color = getColorByString(name || email || emptyName)
  }

  return getInitialAvatarSrcByProps(avatarProps)
}

export function getInitialAvatar(name?: string | null, email?: string | null): ReactNode | null {
  const src = getInitialAvatarSrc(name, email)
  if (src == null) {
    return null
  }

  return <img src={src} alt="" />
}

// ─── formatBytes ─────────────────────────────────────────────────────────────

export function formatBytes(
  bytes: number,
  decimals: number = 2,
  withStyle: boolean = false,
  fixedUnit?: string | number,
  space: string = ' ',
): string | ReactElement {
  let ret: string | ReactElement = '-'
  try {
    if (bytes === 0) return '0'

    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

    let i = 0

    if (fixedUnit != null) {
      i = typeof fixedUnit === 'number' ? fixedUnit : sizes.indexOf(fixedUnit)
    } else {
      i = Math.floor(Math.log(bytes) / Math.log(k))
    }

    const num = window.parseFloat((bytes / k ** i).toFixed(dm))

    if (Number.isNaN(i) === false && num != null) {
      if (withStyle === true) {
        ret = (
          <span className="byte-format">
            <span className="number">{num}</span>
            <span className="space">{space}</span>
            <span className="unit">{sizes[i]}</span>
          </span>
        )
      } else {
        ret = `${num}${space}${sizes[i]}`
      }
    }
  } catch {
    ret = '-'
  }

  return ret
}

// ─── creactReactObjMgr ───────────────────────────────────────────────────────

interface ReactObjMgr {
  reactObjList: any[]
  updateUI: () => void
  forceUpdate: any
  run: (runFunc: (reactObj: any) => void) => void
  addReactObj: (reactObj: any) => void
  removeReactObj: (reactObj: any) => void
}

export function creactReactObjMgr(): ReactObjMgr {
  const reactMgr: ReactObjMgr = {
    reactObjList: [],
    updateUI: () => {
      reactMgr.reactObjList.forEach((reactObj) => {
        if (reactObj.updateState != null) {
          reactObj.updateState()
        } else {
          reactObj.forceUpdate()
        }
      })
    },
    forceUpdate: null,
    run: (runFunc: (reactObj: any) => void) => {
      reactMgr.reactObjList.forEach((reactObj) => {
        runFunc(reactObj)
      })
    },
    addReactObj: (reactObj: any) => {
      if (reactMgr.reactObjList.indexOf(reactObj) < 0) {
        reactMgr.reactObjList.push(reactObj)
      }
    },
    removeReactObj: (reactObj: any) => {
      const pos = reactMgr.reactObjList.indexOf(reactObj)
      if (pos >= 0) {
        reactMgr.reactObjList.splice(pos, 1)
      }
    },
  }

  reactMgr.forceUpdate = reactMgr.updateUI

  return reactMgr
}
